import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { HeaderPro } from '../../components/HeaderPro';
import { Footer } from '../../components/Footer';
import {
  cancelOneAttendance,
  confirmOneAttendance,
  listProAttendance,
  type AttendanceBooking,
} from '../../../lib/pro-attendance-api';
import { apiErrorMessage } from '../../../lib/api';
import { toast } from '../../../lib/toast';

/**
 * Token-authed per-booking management page linked from the 7pm Daily
 * Schedule + 9pm Attendance Chase emails. The token in `?t=` is the
 * only auth — the pro doesn't sign in. Backend:
 *   src/Controllers/Booking/ProBookingAttendance.controller.ts
 *
 * Each booking has three states the page surfaces:
 *   - Attendance confirmed     → green pill, no actions
 *   - CANCELLED_BY_PRO         → red pill, no actions
 *   - Awaiting confirmation    → Confirm + Cancel buttons
 */
export default function AttendanceManagePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') ?? '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scheduledDate, setScheduledDate] = useState<string>('');
  const [bookings, setBookings] = useState<AttendanceBooking[]>([]);
  // Per-booking action state so two simultaneous clicks don't fire.
  const [actingId, setActingId] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelTargetId, setCancelTargetId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) {
      setError(
        'This link is missing its access token. Open it from the email we sent.',
      );
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await listProAttendance(token);
      setScheduledDate(res.scheduled_date ?? '');
      setBookings(res.data ?? []);
    } catch (err) {
      setError(
        apiErrorMessage(
          err,
          'This link is invalid or has expired. Open the latest email we sent.',
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const handleConfirm = async (b: AttendanceBooking) => {
    setActingId(b.id);
    try {
      const res = await confirmOneAttendance(b.id, token);
      if (res.already_confirmed) {
        toast.success('This booking was already confirmed.');
      } else {
        toast.success(`Confirmed ${b.reference}.`);
      }
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not confirm this booking.'));
    } finally {
      setActingId(null);
    }
  };

  const handleCancel = async (b: AttendanceBooking) => {
    setActingId(b.id);
    try {
      await cancelOneAttendance(b.id, token, cancelReason || undefined);
      toast.success(`Cancelled ${b.reference}.`);
      setCancelTargetId(null);
      setCancelReason('');
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not cancel this booking.'));
    } finally {
      setActingId(null);
    }
  };

  const formattedDate = useMemo(() => {
    if (!scheduledDate) return '';
    try {
      return new Date(`${scheduledDate}T00:00:00`).toLocaleDateString(
        undefined,
        {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        },
      );
    } catch {
      return scheduledDate;
    }
  }, [scheduledDate]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <HeaderPro />
      <main className="flex-1 max-w-[820px] w-full mx-auto px-4 py-10">
        <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#3a3335] text-[28px] md:text-[34px] leading-[40px] md:leading-[48px] tracking-[-0.094px] mb-2">
          Manage tomorrow's bookings
        </h1>
        {formattedDate ? (
          <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] mb-8">
            {formattedDate}
          </p>
        ) : null}

        {loading ? (
          <p className="text-center text-[#908e8e] py-12">Loading your bookings…</p>
        ) : error ? (
          <div className="border border-[#ede8e9] rounded-[12px] p-6 bg-[#fcfaf7] text-center">
            <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px]">
              {error}
            </p>
          </div>
        ) : bookings.length === 0 ? (
          <div className="border border-[#ede8e9] rounded-[12px] p-6 text-center text-[#908e8e] text-[14px]">
            No bookings scheduled for this day.
          </div>
        ) : (
          <div className="space-y-3">
            {bookings.map((b) => {
              const confirmed = !!b.attendance_confirmed_at;
              const declined = b.status === 'CANCELLED_BY_PRO';
              const showCancelForm = cancelTargetId === b.id;
              return (
                <div
                  key={b.id}
                  className={`border rounded-[12px] p-5 ${
                    declined
                      ? 'border-red-200 bg-red-50/50'
                      : confirmed
                        ? 'border-emerald-200 bg-emerald-50/40'
                        : 'border-[#ede8e9] bg-white'
                  }`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-mono text-[#3a3335] text-[13px] font-bold">
                        {b.reference}
                      </p>
                      <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[16px] mt-1">
                        {b.service_name || 'Visit'}
                      </h2>
                      <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px] mt-1">
                        {new Date(b.scheduled_start).toLocaleTimeString(
                          undefined,
                          { hour: 'numeric', minute: '2-digit' },
                        )}{' '}
                        · {b.duration_minutes} min
                      </p>
                    </div>
                    {confirmed ? (
                      <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-[11px] font-semibold text-emerald-800">
                        Confirmed
                      </span>
                    ) : declined ? (
                      <span className="inline-flex items-center rounded-full bg-red-100 px-3 py-1 text-[11px] font-semibold text-red-800">
                        Declined
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-[11px] font-semibold text-amber-800">
                        Awaiting your reply
                      </span>
                    )}
                  </div>

                  <div className="mt-3 space-y-1">
                    {b.client_name ? (
                      <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[13px]">
                        Client: {b.client_name}
                      </p>
                    ) : null}
                    {b.address ? (
                      <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px]">
                        {b.address}
                      </p>
                    ) : null}
                  </div>

                  {!confirmed && !declined ? (
                    <>
                      <div className="mt-4 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => handleConfirm(b)}
                          disabled={actingId === b.id}
                          className="bg-[#8e2a2f] text-white font-['DM_Sans',sans-serif] font-bold text-[13px] rounded-[8px] px-4 h-[40px] disabled:opacity-50"
                        >
                          {actingId === b.id ? 'Confirming…' : 'Confirm attendance'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setCancelTargetId(b.id);
                            setCancelReason('');
                          }}
                          disabled={actingId === b.id}
                          className="border border-[#ede8e9] text-[#3a3335] font-['DM_Sans',sans-serif] font-bold text-[13px] rounded-[8px] px-4 h-[40px] disabled:opacity-50"
                        >
                          Cancel this booking
                        </button>
                      </div>
                      {showCancelForm ? (
                        <div className="mt-3 border-t border-[#ede8e9] pt-3 space-y-2">
                          <label className="block font-['DM_Sans',sans-serif] text-[#3a3335] text-[12px] font-bold">
                            Reason (shared with admin only)
                          </label>
                          <textarea
                            rows={3}
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            className="w-full border border-[#ede8e9] rounded-[8px] px-3 py-2 text-[13px] font-['DM_Sans',sans-serif] focus:border-[#8e2a2f] outline-none"
                            placeholder="e.g. Family emergency"
                          />
                          <div className="flex flex-wrap gap-2 pt-1">
                            <button
                              type="button"
                              onClick={() => handleCancel(b)}
                              disabled={actingId === b.id}
                              className="bg-[#8e2a2f] text-white font-['DM_Sans',sans-serif] font-bold text-[13px] rounded-[8px] px-4 h-[36px] disabled:opacity-50"
                            >
                              {actingId === b.id ? 'Cancelling…' : 'Confirm cancellation'}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setCancelTargetId(null);
                                setCancelReason('');
                              }}
                              disabled={actingId === b.id}
                              className="text-[#908e8e] font-['DM_Sans',sans-serif] font-bold text-[13px] px-2 h-[36px] disabled:opacity-50"
                            >
                              Keep this booking
                            </button>
                          </div>
                          <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[11px] pt-1">
                            The customer will be refunded in full and notified automatically.
                          </p>
                        </div>
                      ) : null}
                    </>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
